import React, { useContext } from 'react';
import classnames from 'classnames';
import { ThemeOverride } from '@wix/da-react-context/pkg/ThemeContext';
import { ThemeOptions } from '@wix/da-react-context/pkg/constants';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import ConditionalWrapper from '@wix/da-ds/pkg/ConditionalWrapper';
import { usePopoverOnInteraction } from '@wix/da-ds/pkg/popper/hooks/usePopoverOnInteraction';
import DefaultPopoverContent from '@wix/da-ds/pkg/popper/_partials/DefaultPopoverContent';
import type {
  OffsetModifier,
  PopperPositioningProps,
  ReferenceChildrenProps,
} from '@wix/da-ds/pkg/popper/types';
import TextTooltipContent from '../../_partials/TextTooltipContent';
import PopperPositionedPopover from '@wix/da-ds/pkg/popper/PopperPositionedPopover';
import s from './TextTooltipOnHover.scss';

const defaultOffsetModifier: OffsetModifier = [0, 6];

export interface Props extends PopperPositioningProps {
  title?: string;
  tooltipText?: React.ReactNode;
  className?: string;
  textClassName?: string;
  contentClassName?: string;
  wrapperClassName?: string;
  oneLine?: boolean;
  children?: React.ReactNode;
  listenOnClicks?: boolean;
  forceTheme?: ThemeOptions;
  tooltipOffset?: OffsetModifier;
  width?: number;
  noMinWidth?: boolean;
  onHide?: () => void;
  hoverDelay?: number;
  /**
   * ONLY USE THIS IF YOU KNOW WHAT YOU ARE DOING
   * By default, children passed to this component will be the reference element for the popper,
   * or alternatively you can pass a renderReference prop. This is helpful if you need to make the
   * reference element be some inner node on the entire structure of the tooltip.
   */
  renderReference?: (props: ReferenceChildrenProps) => React.ReactNode;
}

/** @deprecated */
const TextTooltipOnHover: React.FC<React.PropsWithChildren<Props>> = ({
  title,
  tooltipText,
  className,
  textClassName,
  contentClassName,
  wrapperClassName,
  listenOnClicks,
  forceTheme,
  width,
  noMinWidth,
  children,
  placement,
  secondaryPlacement,
  oneLine,
  onHide,
  renderReference,
  referenceElement,
  positionFixed,
  eventsEnabled,
  isVisible: isVisibleProp = true,
  // Design indicates popovers should be separated 8px away from their reference point.
  // Since most icon boxes and/or text have some amount of blank space around them, setting
  // the default popover offset to 6px seems to achieve a look pretty close to spec in general.
  // In other cases it can be overriden if it really needs fixing.
  offsetModifier = defaultOffsetModifier,
  hoverDelay,
}) => {
  const isMobile = useContext(MobileContext);
  const { isVisible, onClick, onMouseEnter, onMouseLeave } = usePopoverOnInteraction({
    listenOnClicks,
    listenOnHover: !isMobile,
    onHide,
    hoverDelay,
  });

  if (!tooltipText && !title) {
    return <>{children}</>;
  }

  return (
    <PopperPositionedPopover
      isVisible={isVisible && isVisibleProp}
      offsetModifier={offsetModifier}
      eventsEnabled={eventsEnabled && isVisible}
      positionFixed={positionFixed}
      referenceElement={referenceElement}
      renderReference={renderReference}
      usePortal
      className={classnames(s['children-wrapper'], className)}
      placement={placement}
      secondaryPlacement={secondaryPlacement}
      disablePlacementModifier
      onReferenceClick={onClick}
      onReferenceMouseEnter={onMouseEnter}
      onReferenceMouseLeave={onMouseLeave}
      renderPopover={({
        effectivePlacement,
        effectiveSecondaryPlacement,
        contentStyle,
        arrowStyle,
        contentAttributes,
        arrowAttributes,
        rootRef,
        arrowRef,
        updatePositioning,
      }) => (
        <ConditionalWrapper
          condition={!!forceTheme}
          wrap={child => <ThemeOverride theme={forceTheme!}>{child}</ThemeOverride>}
        >
          <DefaultPopoverContent
            effectivePlacement={effectivePlacement}
            effectiveSecondaryPlacement={effectiveSecondaryPlacement}
            rootRef={rootRef}
            arrowRef={arrowRef}
            contentStyle={contentStyle}
            arrowStyle={arrowStyle}
            contentAttributes={contentAttributes}
            arrowAttributes={arrowAttributes}
            showArrow
            updatePositioning={updatePositioning}
            wrapperClassName={classnames(s['root'], isVisible && s['visible'], wrapperClassName)}
            contentClassName={classnames(s['content-classname'], contentClassName)}
            oneLine={oneLine}
            width={width}
            noMinWidth={noMinWidth}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
          >
            <TextTooltipContent
              title={title}
              tooltipText={tooltipText}
              textClassName={textClassName}
            />
          </DefaultPopoverContent>
        </ConditionalWrapper>
      )}
    >
      {children}
    </PopperPositionedPopover>
  );
};
TextTooltipOnHover.displayName = 'TextTooltipOnHover';

export default TextTooltipOnHover;
